/**
 * Search Commands & Navigation Index
 *
 * Spotlight-like command palette functionality:
 * - Quick commands (actions, navigation, create shortcuts)
 * - Page navigation index
 * - Category prefix filters
 * - Fuzzy matching utilities
 */

import type { Module } from "@/lib/permissions";

// Command types
export type CommandCategory =
  | "navigation"   // Go to pages
  | "create"       // Create new records
  | "action"       // Quick actions
  | "settings"     // Settings shortcuts
  | "help";        // Help & support

export interface SearchCommand {
  id: string;
  label: string;
  description?: string;
  category: CommandCategory;
  keywords: string[];        // Additional search terms
  icon: string;             // Lucide icon name
  action: CommandAction;
  requiredModule?: Module;
  staffOnly?: boolean;
  /** Platform admin (useUserRoles().isPlatformAdmin), not organization admin. */
  adminOnly?: boolean;
  /**
   * Organization owner/admin (usePermissions().isOrgAdmin).
   *
   * Distinct from `adminOnly`, which gates on PLATFORM admin. A command whose
   * destination sits behind `OrgAdminRoute` needs this one: gating it on
   * `adminOnly` would hide it from the org admins it is for, and leaving it
   * ungated sends any staff member with the module to /403.
   */
  orgAdminOnly?: boolean;
}

export type CommandAction =
  | { type: "navigate"; path: string }
  | { type: "create"; entityType: string; path: string }
  | { type: "action"; actionId: string }
  | { type: "external"; url: string };

// Category prefix mappings for filtered search
export const CATEGORY_PREFIXES: Record<string, { types: string[]; label: string; icon: string }> = {
  "p:": { types: ["person"], label: "People", icon: "User" },
  "h:": { types: ["household"], label: "Households", icon: "Home" },
  "r:": { types: ["request"], label: "Requests", icon: "MessageSquare" },
  "t:": { types: ["task"], label: "Tasks", icon: "CheckSquare" },
  "pr:": { types: ["project"], label: "Projects", icon: "FolderKanban" },
  "c:": { types: ["contribution"], label: "Contributions", icon: "DollarSign" },
  "f:": { types: ["fund"], label: "Funds", icon: "Wallet" },
  "a:": { types: ["asset"], label: "Assets", icon: "Package" },
  "e:": { types: ["event"], label: "Events", icon: "Calendar" },
  "pl:": { types: ["pledge"], label: "Pledges", icon: "HandHeart" },
  "ca:": { types: ["campaign"], label: "Campaigns", icon: "Target" },
  "s:": { types: ["service"], label: "Services", icon: "Church" },
  "@": { types: ["navigation"], label: "Pages", icon: "Navigation" },
  "/": { types: ["command"], label: "Commands", icon: "Terminal" },
  ">": { types: ["action"], label: "Actions", icon: "Zap" },
  "?": { types: ["solomon"], label: "Ask Solomon", icon: "Sparkles" },
};

// Parse query to extract prefix filters
export function parseSearchQuery(query: string): {
  prefix: string | null;
  searchTerm: string;
  filteredTypes: string[] | null;
} {
  const trimmed = query.trim();

  // Check for prefixes (sorted by length desc to match longer prefixes first)
  const sortedPrefixes = Object.keys(CATEGORY_PREFIXES).sort((a, b) => b.length - a.length);

  for (const prefix of sortedPrefixes) {
    if (trimmed.toLowerCase().startsWith(prefix)) {
      return {
        prefix,
        searchTerm: trimmed.slice(prefix.length).trim(),
        filteredTypes: CATEGORY_PREFIXES[prefix].types,
      };
    }
  }

  return {
    prefix: null,
    searchTerm: trimmed,
    filteredTypes: null,
  };
}

// Navigation pages index
export const NAVIGATION_PAGES: SearchCommand[] = [
  // Dashboards
  {
    id: "nav-operations-dashboard",
    label: "Operations Dashboard",
    description: "View operations overview",
    category: "navigation",
    keywords: ["home", "main", "dashboard", "overview"],
    icon: "LayoutDashboard",
    action: { type: "navigate", path: "/" },
    requiredModule: "projects",
  },
  {
    id: "nav-finance-dashboard",
    label: "Finance Dashboard",
    description: "View financial overview",
    category: "navigation",
    keywords: ["money", "giving", "contributions", "dashboard"],
    icon: "DollarSign",
    action: { type: "navigate", path: "/finance/dashboard" },
    requiredModule: "contributions",
  },
  {
    id: "nav-engagement-dashboard",
    label: "Engagement Dashboard",
    description: "View engagement metrics",
    category: "navigation",
    keywords: ["community", "activity", "dashboard"],
    icon: "HeartHandshake",
    action: { type: "navigate", path: "/engagement" },
    requiredModule: "people",
  },
  {
    id: "nav-staff-today",
    label: "Staff Today",
    description: "Your daily overview",
    category: "navigation",
    keywords: ["today", "home", "my day", "staff"],
    icon: "Home",
    action: { type: "navigate", path: "/today" },
    staffOnly: true,
  },

  // People
  {
    id: "nav-people",
    label: "People & Households",
    description: "Manage community members",
    category: "navigation",
    keywords: ["directory", "members", "community", "contacts"],
    icon: "Users",
    action: { type: "navigate", path: "/people" },
    requiredModule: "people",
  },
  {
    id: "nav-directory",
    label: "Directory",
    description: "Browse member directory",
    category: "navigation",
    keywords: ["people", "list", "search", "browse"],
    icon: "Users",
    action: { type: "navigate", path: "/community/directory" },
    requiredModule: "people",
  },

  // HR
  {
    id: "nav-hr",
    label: "HR Overview",
    description: "Human resources dashboard",
    category: "navigation",
    keywords: ["staff", "employees", "human resources"],
    icon: "UserCog",
    action: { type: "navigate", path: "/hr" },
    requiredModule: "hr",
  },
  {
    id: "nav-hr-staff",
    label: "Staff Directory",
    description: "Manage staff members",
    category: "navigation",
    keywords: ["employees", "team", "personnel"],
    icon: "BadgeCheck",
    action: { type: "navigate", path: "/hr/staff" },
    requiredModule: "hr",
  },
  {
    id: "nav-hr-positions",
    label: "Positions",
    description: "Manage job positions",
    category: "navigation",
    keywords: ["jobs", "roles", "hiring"],
    icon: "Briefcase",
    action: { type: "navigate", path: "/hr/positions" },
    requiredModule: "hr",
  },
  {
    id: "nav-hr-time-off",
    label: "Time Off",
    description: "Manage time off requests",
    category: "navigation",
    keywords: ["vacation", "pto", "leave", "absence"],
    icon: "Clock",
    action: { type: "navigate", path: "/hr/time-off" },
    requiredModule: "hr",
  },

  // Finance
  {
    id: "nav-contributions",
    label: "Contributions",
    description: "View and manage contributions",
    category: "navigation",
    keywords: ["giving", "donations", "money", "tithes", "offerings"],
    icon: "DollarSign",
    action: { type: "navigate", path: "/contributions" },
    requiredModule: "contributions",
  },
  {
    id: "nav-pledges",
    label: "Pledges & Campaigns",
    description: "Manage pledges and campaigns",
    category: "navigation",
    keywords: ["promises", "commitments", "fundraising"],
    icon: "HeartHandshake",
    action: { type: "navigate", path: "/pledges" },
    requiredModule: "pledges",
  },
  {
    id: "nav-funds",
    label: "Funds",
    description: "Manage fund categories",
    category: "navigation",
    keywords: ["accounts", "categories", "buckets"],
    icon: "FolderKanban",
    action: { type: "navigate", path: "/funds" },
    requiredModule: "funds",
  },

  // Ministry
  {
    id: "nav-services",
    label: "Services",
    description: "Manage worship services",
    category: "navigation",
    keywords: ["worship", "church", "mass", "sunday"],
    icon: "Church",
    action: { type: "navigate", path: "/services" },
    requiredModule: "services",
  },
  {
    id: "nav-volunteers",
    label: "Volunteers",
    description: "Manage volunteer schedules",
    category: "navigation",
    keywords: ["schedules", "availability", "gaps"],
    icon: "CalendarCheck",
    action: { type: "navigate", path: "/volunteers" },
    requiredModule: "services",
  },
  {
    // NOTE: the label intentionally reads "Serving Teams" so a multi-word query
    // ("serving teams") scores an exact label match in calculateFuzzyScore. The
    // fuzzy matcher scores the whole query phrase against each field, so single-word
    // keywords alone do not surface multi-word searches.
    id: "nav-serving-teams",
    label: "Serving Teams",
    description: "Manage serving teams, members, roles, and positions",
    category: "navigation",
    keywords: ["serving", "teams", "roster", "members", "positions", "volunteers"],
    icon: "Users",
    action: { type: "navigate", path: "/serving-teams" },
    requiredModule: "services",
  },
  {
    id: "nav-attendance",
    label: "Attendance",
    description: "Track attendance records",
    category: "navigation",
    keywords: ["check-in", "headcount", "tracking"],
    icon: "CalendarCheck",
    action: { type: "navigate", path: "/attendance" },
    requiredModule: "attendance",
  },
  {
    id: "nav-kids-checkin",
    label: "Kids Check-In",
    description: "Children's ministry check-in",
    category: "navigation",
    keywords: ["children", "childcare", "nursery", "sunday school"],
    icon: "Baby",
    action: { type: "navigate", path: "/kids-check-in" },
    requiredModule: "childcare",
  },

  // Operations
  {
    id: "nav-requests",
    label: "Care Requests",
    description: "View pastoral care requests",
    category: "navigation",
    keywords: ["pastoral", "care", "help", "support", "needs"],
    icon: "HandHeart",
    action: { type: "navigate", path: "/requests" },
    requiredModule: "requests",
  },
  {
    id: "nav-tasks",
    label: "Tasks",
    description: "Manage your tasks",
    category: "navigation",
    keywords: ["todo", "work", "assignments", "checklist"],
    icon: "CheckSquare",
    action: { type: "navigate", path: "/tasks" },
    requiredModule: "tasks",
  },
  {
    id: "nav-projects",
    label: "Projects",
    description: "Manage projects and initiatives",
    category: "navigation",
    keywords: ["initiatives", "programs", "planning"],
    icon: "FolderKanban",
    action: { type: "navigate", path: "/projects" },
    requiredModule: "projects",
  },
  {
    id: "nav-assets",
    label: "Assets",
    description: "Track inventory and assets",
    category: "navigation",
    keywords: ["inventory", "equipment", "property"],
    icon: "Package",
    action: { type: "navigate", path: "/assets" },
    requiredModule: "assets",
  },

  // Messaging
  {
    id: "nav-messaging",
    label: "Messaging Hub",
    description: "Send messages and emails",
    category: "navigation",
    keywords: ["email", "sms", "text", "communicate"],
    icon: "MessageSquare",
    action: { type: "navigate", path: "/messaging" },
    requiredModule: "messaging",
  },
  {
    id: "nav-announcements",
    label: "Announcements",
    description: "Manage announcements",
    category: "navigation",
    keywords: ["news", "updates", "broadcast"],
    icon: "Megaphone",
    action: { type: "navigate", path: "/announcements" },
    requiredModule: "announcements",
    staffOnly: true,
  },
  {
    id: "nav-bulletins",
    label: "Bulletins",
    description: "Create and manage bulletins",
    category: "navigation",
    keywords: ["newsletter", "weekly", "publication"],
    icon: "Newspaper",
    action: { type: "navigate", path: "/bulletins" },
    requiredModule: "announcements",
    staffOnly: true,
  },

  // Calendar & Events
  {
    id: "nav-calendar",
    label: "Calendar",
    description: "View calendar and events",
    category: "navigation",
    keywords: ["schedule", "events", "dates"],
    icon: "Calendar",
    action: { type: "navigate", path: "/calendar" },
  },
  {
    id: "nav-events",
    label: "Events",
    description: "Manage events",
    category: "navigation",
    keywords: ["gatherings", "meetings", "occasions"],
    icon: "Calendar",
    action: { type: "navigate", path: "/events" },
  },

  // Reports
  {
    id: "nav-reports",
    label: "Reports",
    description: "View and run reports",
    category: "navigation",
    keywords: ["analytics", "data", "metrics", "insights"],
    icon: "BarChart3",
    action: { type: "navigate", path: "/reports" },
    requiredModule: "reports",
  },

  // Member Portal
  {
    id: "nav-member-portal",
    label: "Member Portal",
    description: "Member self-service area",
    category: "navigation",
    keywords: ["my account", "profile", "personal"],
    icon: "User",
    action: { type: "navigate", path: "/member" },
  },
  {
    id: "nav-my-profile",
    label: "My Profile",
    description: "View and edit your profile",
    category: "navigation",
    keywords: ["account", "personal", "info"],
    icon: "User",
    action: { type: "navigate", path: "/member/profile" },
  },
  {
    id: "nav-my-giving",
    label: "My Giving History",
    description: "View your giving history",
    category: "navigation",
    keywords: ["contributions", "donations", "statements"],
    icon: "DollarSign",
    action: { type: "navigate", path: "/member/giving" },
  },

  // Module entry points
  {
    id: "nav-member-portal-hub",
    label: "Member Portal Hub",
    description: "Access member self-service portal",
    category: "navigation",
    keywords: ["portal", "self-service", "my account", "member home"],
    icon: "UserCircle",
    action: { type: "navigate", path: "/member" },
  },
  {
    id: "nav-operations-hub",
    label: "Operations Hub",
    description: "Operations and administration overview",
    category: "navigation",
    keywords: ["operations", "admin", "ops", "management"],
    icon: "Building2",
    action: { type: "navigate", path: "/dashboard" },
    requiredModule: "projects",
  },
  {
    id: "nav-ministry-hub",
    label: "Ministry Hub",
    description: "Ministry management overview",
    category: "navigation",
    keywords: ["ministry", "ministries", "church", "pastoral"],
    icon: "Church",
    action: { type: "navigate", path: "/ministry" },
  },
  {
    id: "nav-pastoral-care",
    label: "Pastoral Care",
    description: "Manage pastoral care requests and assignments",
    category: "navigation",
    keywords: ["pastoral", "care", "counseling", "prayer", "hospital", "crisis"],
    icon: "Heart",
    action: { type: "navigate", path: "/pastoral-care" },
  },
  {
    id: "nav-potential-duplicates",
    label: "Potential Duplicates",
    description: "Review and merge duplicate person, household, and family-group records",
    category: "navigation",
    keywords: ["duplicate", "duplicates", "merge", "dedup", "people", "records"],
    icon: "Users",
    // PotentialDuplicates.tsx (a second, smaller duplicate-review UI over the
    // same data) was deprecated in favor of this fuller tool (#6365).
    action: { type: "navigate", path: "/community/merge" },
  },

  // Settings
  {
    id: "nav-settings",
    label: "Settings",
    description: "Organization settings",
    category: "navigation",
    keywords: ["preferences", "config", "admin"],
    icon: "Settings",
    action: { type: "navigate", path: "/settings" },
    staffOnly: true,
  },
  {
    id: "nav-integrations",
    label: "Integrations",
    description: "Manage third-party integrations",
    category: "navigation",
    keywords: ["connections", "apps", "sync"],
    icon: "Plug",
    action: { type: "navigate", path: "/settings/integrations" },
    requiredModule: "integrations",
    staffOnly: true,
    // /settings/integrations is wrapped in OrgAdminRoute (App.tsx), so a
    // non-admin staff member with the integrations module was being offered a
    // command that lands on /403.
    orgAdminOnly: true,
  },
  {
    // The full Stripe Connect surface — start or finish onboarding, check
    // payout status, disconnect. It had no entry in any nav, menu, sidebar or
    // command list, so the only ways in were typing the URL or walking the
    // Integrations card (#5474).
    id: "nav-integrations-stripe",
    label: "Stripe",
    description: "Connect Stripe and manage online giving payouts",
    category: "navigation",
    keywords: ["payments", "payouts", "connect", "giving", "donations", "merchant"],
    icon: "CreditCard",
    action: { type: "navigate", path: "/integrations/stripe" },
    requiredModule: "integrations",
    staffOnly: true,
    // /integrations/stripe is OrgAdminRoute, and every Stripe Connect endpoint
    // enforces organizationRoles: ["owner","admin"] server-side anyway.
    orgAdminOnly: true,
  },
];

// Quick create commands
export const CREATE_COMMANDS: SearchCommand[] = [
  {
    id: "create-person",
    label: "New Person",
    description: "Add a new person to the directory",
    category: "create",
    keywords: ["add person", "new member", "create contact"],
    icon: "UserPlus",
    action: { type: "create", entityType: "person", path: "/people/all?action=add-person" },
    requiredModule: "people",
  },
  {
    id: "create-household",
    label: "New Household",
    description: "Create a new household",
    category: "create",
    keywords: ["add family", "new household", "create household"],
    icon: "Home",
    action: { type: "create", entityType: "household", path: "/people/all?action=add-household" },
    requiredModule: "people",
  },
  {
    id: "view-family-groups",
    label: "Family Groups",
    description: "View family groups",
    // Was "navigate", which is not a CommandCategory — a standing TS2322 in
    // this file. Corrected in passing while adding the Stripe entry below.
    category: "navigation",
    keywords: ["family group", "family", "extended family", "multi-generational"],
    icon: "Users",
    action: { type: "navigate", path: "/people" },
    requiredModule: "people",
  },
  {
    id: "create-contribution",
    label: "New Contribution",
    description: "Record a new contribution",
    category: "create",
    keywords: ["add donation", "record giving", "new gift"],
    icon: "DollarSign",
    action: { type: "create", entityType: "contribution", path: "/contributions/new" },
    requiredModule: "contributions",
  },
  {
    id: "create-request",
    label: "New Care Request",
    description: "Submit a care request",
    category: "create",
    keywords: ["add request", "pastoral care", "help needed"],
    icon: "HandHeart",
    action: { type: "create", entityType: "request", path: "/requests?action=new" },
    requiredModule: "requests",
  },
  {
    id: "create-task",
    label: "New Task",
    description: "Create a new task",
    category: "create",
    keywords: ["add todo", "new assignment", "create task"],
    icon: "CheckSquare",
    action: { type: "create", entityType: "task", path: "/tasks?action=new" },
    requiredModule: "tasks",
  },
  {
    id: "create-project",
    label: "New Project",
    description: "Start a new project",
    category: "create",
    keywords: ["add project", "new initiative"],
    icon: "FolderKanban",
    action: { type: "create", entityType: "project", path: "/projects?action=new" },
    requiredModule: "projects",
  },
  {
    id: "create-event",
    label: "New Event",
    description: "Create a new event",
    category: "create",
    keywords: ["add event", "schedule event", "new meeting"],
    icon: "Calendar",
    action: { type: "create", entityType: "event", path: "/calendar?action=new" },
  },
  {
    id: "create-announcement",
    label: "New Announcement",
    description: "Create a new announcement",
    category: "create",
    keywords: ["add announcement", "broadcast", "news"],
    icon: "Megaphone",
    action: { type: "create", entityType: "announcement", path: "/announcements/new" },
    requiredModule: "announcements",
    staffOnly: true,
  },
  {
    id: "create-bulletin",
    label: "New Bulletin",
    description: "Create a new bulletin",
    category: "create",
    keywords: ["add bulletin", "newsletter", "weekly"],
    icon: "Newspaper",
    action: { type: "create", entityType: "bulletin", path: "/bulletins/new" },
    requiredModule: "announcements",
    staffOnly: true,
  },
];

// Quick action commands
export const ACTION_COMMANDS: SearchCommand[] = [
  {
    id: "action-quick-add",
    label: "Quick Add",
    description: "Open the quick add menu",
    category: "action",
    keywords: ["add", "create", "new", "plus"],
    icon: "Plus",
    action: { type: "action", actionId: "quick-add" },
  },
  {
    id: "action-refresh",
    label: "Refresh Data",
    description: "Reload current page data",
    category: "action",
    keywords: ["reload", "sync", "update"],
    icon: "RefreshCw",
    action: { type: "action", actionId: "refresh" },
  },
  {
    id: "action-keyboard-shortcuts",
    label: "Keyboard Shortcuts",
    description: "View all keyboard shortcuts",
    category: "action",
    keywords: ["hotkeys", "keys", "shortcuts"],
    icon: "Keyboard",
    action: { type: "action", actionId: "show-shortcuts" },
  },
];

// Settings shortcuts
export const SETTINGS_COMMANDS: SearchCommand[] = [
  {
    id: "settings-organization",
    label: "Organization Settings",
    description: "Manage organization details",
    category: "settings",
    keywords: ["org", "company", "church"],
    icon: "Building2",
    action: { type: "navigate", path: "/settings/organization" },
    staffOnly: true,
  },
  {
    id: "settings-team",
    label: "Team Settings",
    description: "Manage team members and roles",
    category: "settings",
    keywords: ["users", "members", "access"],
    icon: "Users",
    action: { type: "navigate", path: "/settings/team" },
    staffOnly: true,
  },
  {
    id: "settings-permissions",
    label: "Permissions",
    description: "Manage user permissions",
    category: "settings",
    keywords: ["access", "roles", "security"],
    icon: "Shield",
    action: { type: "navigate", path: "/settings/permissions" },
    staffOnly: true,
  },
  {
    id: "settings-audit",
    label: "Audit Logs",
    description: "View activity audit logs",
    category: "settings",
    keywords: ["history", "activity", "logs"],
    icon: "FileText",
    action: { type: "navigate", path: "/settings/audit" },
    staffOnly: true,
  },
  {
    id: "settings-imports",
    label: "Data Imports",
    description: "Import data from files",
    category: "settings",
    keywords: ["upload", "csv", "import"],
    icon: "Upload",
    action: { type: "navigate", path: "/settings/import" },
    staffOnly: true,
  },
];

// Help commands
export const HELP_COMMANDS: SearchCommand[] = [
  {
    id: "help-docs",
    label: "Help Documentation",
    description: "View help and documentation",
    category: "help",
    keywords: ["docs", "guide", "manual", "faq"],
    icon: "HelpCircle",
    action: { type: "external", url: "https://forgehq.io/docs" },
  },
  {
    id: "help-support",
    label: "Contact Support",
    description: "Get help from our support team",
    category: "help",
    keywords: ["contact", "help", "ticket"],
    icon: "MessageCircle",
    action: { type: "external", url: "mailto:support@forgehq.io" },
  },
  {
    id: "help-feedback",
    label: "Send Feedback",
    description: "Share your feedback",
    category: "help",
    keywords: ["suggest", "feedback", "ideas"],
    icon: "MessageSquarePlus",
    action: { type: "action", actionId: "feedback" },
  },
];

// All commands combined
export const ALL_COMMANDS: SearchCommand[] = [
  ...NAVIGATION_PAGES,
  ...CREATE_COMMANDS,
  ...ACTION_COMMANDS,
  ...SETTINGS_COMMANDS,
  ...HELP_COMMANDS,
];

// Fuzzy matching score calculation
export function calculateFuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact match
  if (t === q) return 100;

  // Starts with query
  if (t.startsWith(q)) return 90;

  // Contains query as whole word
  if (t.includes(` ${q}`) || t.includes(`${q} `)) return 80;

  // Contains query
  if (t.includes(q)) return 70;

  // Check each word in target
  const targetWords = t.split(/\s+/);
  for (const word of targetWords) {
    if (word.startsWith(q)) return 65;
  }

  // Fuzzy character matching (for typo tolerance)
  let score = 0;
  let queryIndex = 0;
  let consecutiveMatches = 0;

  for (let i = 0; i < t.length && queryIndex < q.length; i++) {
    if (t[i] === q[queryIndex]) {
      queryIndex++;
      consecutiveMatches++;
      score += consecutiveMatches; // Bonus for consecutive matches
    } else {
      consecutiveMatches = 0;
    }
  }

  // Only return fuzzy score if we matched all query characters
  if (queryIndex === q.length) {
    const matchRatio = q.length / t.length;
    return Math.min(60, Math.floor(score * matchRatio * 10));
  }

  return 0;
}

// Search through commands with fuzzy matching
export function searchCommands(
  query: string,
  commands: SearchCommand[],
  options?: {
    canAccessModule?: (module: Module) => boolean;
    isStaff?: boolean;
    isAdmin?: boolean;
    isOrgAdmin?: boolean;
  }
): Array<SearchCommand & { score: number }> {
  if (!query.trim()) return [];

  const results: Array<SearchCommand & { score: number }> = [];

  for (const command of commands) {
    // Filter by permissions
    if (command.requiredModule && options?.canAccessModule) {
      if (!options.canAccessModule(command.requiredModule)) continue;
    }
    if (command.staffOnly && !options?.isStaff) continue;
    if (command.adminOnly && !options?.isAdmin) continue;
    if (command.orgAdminOnly && !options?.isOrgAdmin) continue;

    // Calculate scores for label, description, and keywords
    let maxScore = calculateFuzzyScore(query, command.label);

    if (command.description) {
      maxScore = Math.max(maxScore, calculateFuzzyScore(query, command.description) * 0.8);
    }

    for (const keyword of command.keywords) {
      maxScore = Math.max(maxScore, calculateFuzzyScore(query, keyword) * 0.9);
    }

    if (maxScore > 0) {
      results.push({ ...command, score: maxScore });
    }
  }

  // Sort by score descending
  return results.sort((a, b) => b.score - a.score);
}

// Category labels and icons for display
export const COMMAND_CATEGORY_META: Record<CommandCategory, { label: string; icon: string }> = {
  navigation: { label: "Go to", icon: "Navigation" },
  create: { label: "Create", icon: "Plus" },
  action: { label: "Actions", icon: "Zap" },
  settings: { label: "Settings", icon: "Settings" },
  help: { label: "Help", icon: "HelpCircle" },
};
